// adminportal/src/pages/PasswordReset.tsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, User, Shield } from 'lucide-react';
import QRCode from 'qrcode';
import Card from '../components/Card';
import Button from '../components/Button';
import Copyright from '../components/Copyright';
import Logo from '../components/Logo';
import { apiUrl } from '../config';
import { CognitoAuthService } from '../services/cognitoAuth';
import { MfaService } from '../services/mfaService';
import { usePageTitle } from '../hooks/usePageTitle';
import { loadCognitoConfig } from '../config/cognito';

console.log('🔍 PasswordReset component loaded');

interface LoginForm {
  username: string;
  password: string;
  tempPassword?: string;
  confirmationCode?: string;
  newPassword?: string;
  confirmPassword?: string;
}

type LoginStep =
  | 'login'
  | 'temp-login'
  | 'forgot-password'
  | 'reset-password'
  | 'change-password'
  | 'mfa-setup'
  | 'password-reset-success';

export default function PasswordReset() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>('login');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [user, setUser] = useState<{ firstName: string; lastName: string; role: string } | null>(
    null
  );
  const [session, setSession] = useState<string | null>(null);
  const [mfaSecret, setMfaSecret] = useState<string>('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');

  // MFA challenge state
  const [mfaSession, setMfaSession] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [showMfaForm, setShowMfaForm] = useState(false);
  const [mfaUsername, setMfaUsername] = useState<string>('');

  const [form, setForm] = useState<LoginForm>({
    username: searchParams.get('username') || '',
    password: '',
    tempPassword: '',
    confirmationCode: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Debug: Check localStorage on page load
  useEffect(() => {
    console.log('🔍 Password Reset Page Load Debug:');
    console.log('  - All localStorage keys:', Object.keys(localStorage));
    console.log(
      '  - cognito_access_token:',
      localStorage.getItem('cognito_access_token') ? 'Present' : 'Missing'
    );
    console.log(
      '  - cognito_refresh_token:',
      localStorage.getItem('cognito_refresh_token') ? 'Present' : 'Missing'
    );
    console.log(
      '  - cognito_id_token:',
      localStorage.getItem('cognito_id_token') ? 'Present' : 'Missing'
    );
    console.log('  - authToken:', localStorage.getItem('authToken') ? 'Present' : 'Missing');
    console.log('  - Current step:', step);
    console.log('  - User:', user);
    console.log('  - Session:', session);

    // Load Cognito configuration
    const initializeCognito = async () => {
      try {
        await loadCognitoConfig();
        console.log('✅ Cognito configuration loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load Cognito configuration:', error);
      }
    };

    initializeCognito();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle login (works for both regular and temporary passwords)
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (!form.username || !form.password) {
      setMessage('❌ Username and password are required');
      setLoading(false);
      return;
    }

    try {
      console.log('🔍 PasswordReset Login Debug:');
      console.log('  - Username:', form.username);
      console.log('  - Password length:', form.password?.length);

      // Check if Cognito config is loaded
      if (!(window as any).__COGNITO_CONFIG) {
        console.error('❌ Cognito config not loaded! Attempting to reload config...');
        try {
          const { loadConfig, setGlobalConfig } = await import('../config');
          const cfg = await loadConfig();
          setGlobalConfig(cfg);
          console.log('✅ Config reloaded successfully');
        } catch (configError) {
          console.error('❌ Failed to reload config:', configError);
          setMessage('❌ Configuration error. Please refresh the page and try again.');
          setLoading(false);
          return;
        }
      }

      // Use Cognito authentication instead of direct API call
      const result = await CognitoAuthService.login(form.username, form.password);

      console.log('🔍 Login result:', result);

      if (result.success) {
        // Login successful - always go to password management page
        setUser(result.user);
        // Store user type for later use
        localStorage.setItem('userType', result.userType || 'user');
        setStep('change-password');
        setMessage('🔐 You can change your password and set up MFA');
      } else if (result.challengeName === 'SOFTWARE_TOKEN_MFA') {
        // MFA challenge required
        console.log('🔍 MFA challenge required, showing MFA form');
        console.log('🔍 Current step:', step);
        setMfaSession(result.session || null);
        setMfaUsername(form.username); // Store the username for MFA challenge
        setShowMfaForm(true);
        setMessage('Please enter your MFA code to continue.');
        console.log('🔍 MFA form should now be visible');
      } else if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // Forced password change (NEW_PASSWORD_REQUIRED challenge)
        setUser(result.user);
        if (result.session) {
          setSession(result.session);
        }
        setStep('change-password');
        setMessage('🔐 Please set your permanent password');
      } else {
        // Login failed - show error message
        setMessage(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (err: unknown) {
      console.error('🔍 PasswordReset Login Error:', err);
      console.log('🔍 Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        name: err instanceof Error ? err.name : undefined,
      });
      setMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password request
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (!form.username) {
      setMessage('❌ Username is required');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(apiUrl('forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Password reset request failed');
      }

      setMessage('✅ Password reset code sent to your email');
      setStep('reset-password');
    } catch (err: unknown) {
      setMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset with confirmation code
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (!form.confirmationCode || !form.newPassword || !form.confirmPassword) {
      setMessage('❌ Confirmation code, new password and confirmation are required');
      setLoading(false);
      return;
    }

    if (form.newPassword && form.newPassword !== form.confirmPassword) {
      setMessage('❌ Passwords do not match');
      setLoading(false);
      return;
    }

    if (form.newPassword && form.newPassword.length < 8) {
      setMessage('❌ Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(apiUrl('reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username,
          code: form.confirmationCode,
          newPassword: form.newPassword,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Password reset failed');
      }

      // Clear any existing authentication data
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      sessionStorage.clear();

      // Show success splash page
      setStep('password-reset-success');
      setMessage('');
    } catch (err: unknown) {
      setMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle password change (for users with temporary passwords or wanting to change)
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // For forced password changes, both fields are required
    if (session && (!form.newPassword || !form.confirmPassword)) {
      setMessage('❌ New password and confirmation are required');
      setLoading(false);
      return;
    }

    // For optional password changes, if one field is filled, both must be filled
    if (!session && (form.newPassword || form.confirmPassword)) {
      if (!form.newPassword || !form.confirmPassword) {
        setMessage('❌ Both password fields must be filled if you want to change your password');
        setLoading(false);
        return;
      }
    }

    // If no password change is requested, skip password validation
    if (!session && !form.newPassword && !form.confirmPassword) {
      setMessage('✅ No password change requested. You can set up MFA below.');
      setLoading(false);
      return;
    }

    if (form.newPassword && form.newPassword !== form.confirmPassword) {
      setMessage('❌ Passwords do not match');
      setLoading(false);
      return;
    }

    if (form.newPassword && form.newPassword.length < 8) {
      setMessage('❌ Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      // Only include session if it exists (for forced password changes)
      const requestBody: { username: string; newPassword: string; session?: string } = {
        username: form.username,
        newPassword: form.newPassword || '',
      };

      if (session) {
        requestBody.session = session;
      }

      const res = await fetch(apiUrl('change-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Password change failed');
      }

      // Password change successful
      setMessage('✅ Password updated successfully! You can now log in with your new password.');
      // Store the user data for potential future use
      if (data.user) {
        setUser(data.user);
      }

      // Store authentication tokens if provided (for forced password changes)
      console.log('🔍 Password Change Response Debug:');
      console.log('  - Response data:', data);
      console.log('  - Access Token in response:', data.accessToken ? 'Present' : 'Missing');
      console.log('  - Refresh Token in response:', data.refreshToken ? 'Present' : 'Missing');
      console.log('  - Session:', session);
      console.log('  - User:', data.user);

      if (data.accessToken) {
        localStorage.setItem('cognito_access_token', data.accessToken);
        console.log('🔐 Access token stored for MFA setup');
        console.log(
          '  - Stored token (first 20 chars):',
          `${data.accessToken.substring(0, 20)}...`
        );
      } else {
        console.log('❌ No access token in password change response');
      }
      if (data.refreshToken) {
        localStorage.setItem('cognito_refresh_token', data.refreshToken);
        console.log('🔄 Refresh token stored');
      } else {
        console.log('❌ No refresh token in password change response');
      }

      // Debug: Check what's in localStorage after storing
      console.log('🔍 localStorage after password change:');
      console.log(
        '  - cognito_access_token:',
        localStorage.getItem('cognito_access_token') ? 'Present' : 'Missing'
      );
      console.log(
        '  - cognito_refresh_token:',
        localStorage.getItem('cognito_refresh_token') ? 'Present' : 'Missing'
      );
      console.log('  - All localStorage keys:', Object.keys(localStorage));

      // Authentication tokens are now stored for MFA setup
    } catch (err: unknown) {
      setMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle MFA setup using shared MFA service
  const handleMfaSetup = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    setMessage('');

    const accessToken = localStorage.getItem('cognito_access_token');

    console.log('🔍 PasswordReset MFA Setup Debug:');
    console.log('  - Access Token:', accessToken ? 'Present' : 'Missing');
    console.log('  - User:', user);
    console.log('  - Session:', session);

    if (!accessToken) {
      setMessage('❌ You must be logged in to set up MFA. Please log in first.');
      setLoading(false);
      return;
    }

    try {
      const result = await MfaService.associateSoftwareToken(accessToken);

      if (result.success && result.secretCode) {
        setMfaSecret(result.secretCode);
        setMessage(
          '✅ MFA setup started. Please scan the QR code or enter the secret key in your authenticator app.'
        );
        console.log('🔍 Secret code received:', `${result.secretCode.substring(0, 10)}...`);

        // Generate QR code
        try {
          const qrCodeUrl = await QRCode.toDataURL(
            `otpauth://totp/NGHS-Transfer:${form.username}?secret=${result.secretCode}&issuer=NGHS-Transfer`
          );
          setQrCodeDataUrl(qrCodeUrl);
          console.log('🔍 QR code generated successfully');
        } catch (qrError) {
          console.error('❌ Failed to generate QR code:', qrError);
          // Don't fail the whole process if QR code generation fails
        }
      } else {
        console.log('🔍 No secret code in response:', result);
        setMessage(`❌ ${result.error || 'Failed to get MFA secret. Please try again.'}`);
      }
    } catch (err: unknown) {
      setMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle MFA verification
  const handleMfaVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const accessToken = localStorage.getItem('cognito_access_token');

    if (!accessToken) {
      setMessage('❌ You must be logged in to complete MFA setup.');
      setLoading(false);
      return;
    }

    if (!form.confirmationCode || form.confirmationCode.length !== 6) {
      setMessage('❌ Please enter a valid 6-digit MFA code.');
      setLoading(false);
      return;
    }

    try {
      console.log('🔍 PasswordReset MFA Verification Debug:');
      console.log('  - Access Token:', accessToken ? 'Present' : 'Missing');
      console.log('  - Access Token (first 20 chars):', `${accessToken?.substring(0, 20)}...`);
      console.log('  - MFA Code:', form.confirmationCode);
      console.log('  - MFA Code length:', form.confirmationCode?.length);

      // Test the API call directly first
      console.log('🔍 Testing MFA API call directly...');
      const testResponse = await fetch(apiUrl('mfa/verify-software-token'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken,
          userCode: form.confirmationCode,
        }),
      });

      console.log('🔍 Direct API test response status:', testResponse.status);
      const testData = await testResponse.json();
      console.log('🔍 Direct API test response data:', testData);

      const result = await MfaService.completeMfaSetup(accessToken, form.confirmationCode);

      if (result.success) {
        setMessage('✅ MFA setup completed successfully! You can now use MFA for future logins.');
        setMfaSecret('');
        setForm(prev => ({ ...prev, confirmationCode: '' }));

        // MFA setup completed - go back to password management page
        // Store user type for admin button display
        const userType = localStorage.getItem('userType') || 'user';
        localStorage.setItem('userType', userType);

        setTimeout(() => {
          setStep('change-password');
          setMessage(
            '✅ MFA setup completed! You can now change your password or go to admin portal.'
          );
        }, 2000);
      } else {
        console.log('🔍 MFA verification failed:', result.error);
        setMessage(`❌ ${result.error || 'MFA verification failed. Please try again.'}`);
      }
    } catch (err: unknown) {
      console.error('🔍 MFA verification error:', err);
      setMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle MFA challenge submission
  async function handleMfaChallengeSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!mfaCode || mfaCode.length !== 6) {
      setMessage('❌ Please enter a valid 6-digit MFA code.');
      return;
    }

    if (!mfaSession || !mfaUsername) {
      setMessage('❌ MFA session expired. Please log in again.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      console.log('🔍 PasswordReset MFA Challenge Debug:');
      console.log('  - Session:', mfaSession ? 'Present' : 'Missing');
      console.log('  - MFA Code length:', mfaCode?.length);
      console.log('  - Username:', mfaUsername);

      const result = await CognitoAuthService.respondToMfaChallenge(
        mfaSession,
        mfaCode,
        mfaUsername
      );

      console.log('🔍 PasswordReset MFA Challenge result:', result);

      if (result.success) {
        // MFA successful - hide the MFA form
        setShowMfaForm(false);
        setMfaCode('');
        setMfaSession(null);
        setMfaUsername('');

        // Always go to password management page
        setUser(result.user);
        // Store user type for later use
        localStorage.setItem('userType', result.userType || 'user');
        setStep('change-password');
        setMessage('🔐 You can change your password and set up MFA');
      } else {
        console.log('🔍 MFA challenge failed:', result.error);
        setMessage(`❌ ${result.error || 'MFA verification failed. Please try again.'}`);
      }
    } catch (err: unknown) {
      console.error('🔍 MFA challenge error:', err);
      setMessage(`❌ ${err instanceof Error ? err.message : 'An error occurred'}`);
    } finally {
      setLoading(false);
    }
  }

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, color: 'text-slate-400', label: '' };
    if (password.length < 8) return { strength: 1, color: 'text-red-400', label: 'Weak' };
    if (password.length < 12) return { strength: 2, color: 'text-yellow-400', label: 'Fair' };
    if (password.length < 16) return { strength: 3, color: 'text-blue-400', label: 'Good' };
    return { strength: 4, color: 'text-green-400', label: 'Strong' };
  };

  const passwordStrength = getPasswordStrength(form.newPassword || '');

  const getStepTitle = () => {
    switch (step) {
      case 'login':
        return 'NGHS Transfer Portal - Password Reset';
      case 'forgot-password':
        return '🔍 Forgot Password';
      case 'reset-password':
        return '🔄 Reset Password';
      case 'change-password':
        return '🔐 Set New Password';
      case 'mfa-setup':
        return '🛡️ Setup MFA';
      case 'password-reset-success':
        return '✅ Password Reset Successful';
      default:
        return '🔐 NGHS Transfer Portal';
    }
  };

  const getStepSubtitle = () => {
    switch (step) {
      case 'login':
        return 'Login to reset your password';
      case 'forgot-password':
        return 'Request a password reset code';
      case 'reset-password':
        return 'Enter the code from your email';
      case 'change-password':
        return 'Set your permanent password';
      case 'mfa-setup':
        return 'Enable multi-factor authentication';
      case 'password-reset-success':
        return 'Your password has been successfully reset';
      default:
        return 'Access your transfer account';
    }
  };

  // Set page title based on current step
  usePageTitle(getStepTitle());

  return (
    <div
      className='min-h-screen flex flex-col items-center justify-center p-4'
      style={{ backgroundColor: 'var(--color-bg-primary)' }}
    >
      <div className='w-full max-w-md'>
        {/* Centered Logo */}
        <div className='w-full flex justify-center mb-8'>
          <Logo size='xs' showText={false} />
        </div>

        {/* Centered Title */}
        <div className='text-center mb-6'>
          <h1 className='text-2xl font-bold' style={{ color: 'var(--color-text-primary)' }}>
            {getStepTitle()}
          </h1>
          <p className='text-sm mt-2' style={{ color: 'var(--color-text-secondary)' }}>
            {getStepSubtitle()}
          </p>
        </div>

        <Card>
          {message && (
            <div
              className={`p-4 rounded-lg mb-6 ${
                message.startsWith('✅')
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-rose-500/20 text-rose-400'
              }`}
            >
              {message}
            </div>
          )}

          {/* Main Login Form */}
          {step === 'login' && (
            <form onSubmit={handleLogin} className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>
                  Username or Email *
                </label>
                <div className='relative'>
                  <input
                    type='text'
                    name='username'
                    value={form.username}
                    onChange={handleChange}
                    className='w-full px-3 py-2 pl-12 bg-slate-700 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none focus:bg-slate-700'
                    placeholder='Enter your username or email'
                    autoComplete='username'
                    required
                  />
                  <User className='absolute left-3 top-2.5 h-5 w-5 text-slate-400' />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>Password *</label>
                <div className='relative'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name='password'
                    value={form.password}
                    onChange={handleChange}
                    className='w-full px-3 py-2 pl-12 pr-10 bg-slate-700 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
                    placeholder='Enter your password'
                    autoComplete='current-password'
                    required
                  />
                  <Lock className='absolute left-3 top-2.5 h-5 w-5 text-slate-400' />
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className='absolute right-3 top-2.5 text-slate-400 hover:text-slate-300'
                  >
                    {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
                  </button>
                </div>
              </div>

              <Button
                type='submit'
                disabled={loading || !form.username || !form.password}
                className='w-full'
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>

              <div className='text-center space-y-2'>
                <button
                  type='button'
                  onClick={() => setStep('forgot-password')}
                  className='text-sm text-slate-400 hover:text-slate-300 transition-colors block w-full'
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          )}

          {/* MFA Challenge Form */}
          {showMfaForm && step === 'login' && (
            <div className='mt-6 p-4 bg-slate-800 rounded-lg border-2 border-red-500'>
              <h3 className='text-lg font-medium text-white mb-3'>Multi-Factor Authentication</h3>
              <p className='text-sm text-slate-300 mb-4'>
                Please enter the 6-digit code from your authenticator app.
              </p>
              <form onSubmit={handleMfaChallengeSubmit} className='space-y-3'>
                <div className='relative'>
                  <input
                    type='text'
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value)}
                    className='w-full px-4 py-3 bg-slate-700 border-2 border-slate-600 rounded-lg text-slate-100 focus:border-blue-500 focus:outline-none text-center text-xl tracking-widest font-mono'
                    placeholder='000000'
                    maxLength={6}
                    autoComplete='one-time-code'
                    style={{ minHeight: '48px' }}
                  />
                </div>
                <div className='flex space-x-2'>
                  <Button
                    type='submit'
                    disabled={loading || mfaCode.length !== 6}
                    className='flex-1'
                  >
                    {loading ? 'Verifying...' : 'Verify MFA Code'}
                  </Button>
                  <Button
                    type='button'
                    onClick={() => {
                      setShowMfaForm(false);
                      setMfaCode('');
                      setMfaSession(null);
                      setMfaUsername('');
                    }}
                    variant='outline'
                    className='flex-1'
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Forgot Password */}
          {step === 'forgot-password' && (
            <form onSubmit={handleForgotPassword} className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>
                  Username or Email *
                </label>
                <div className='relative'>
                  <input
                    type='text'
                    name='username'
                    value={form.username}
                    onChange={handleChange}
                    className='w-full px-3 py-2 pl-12 bg-slate-700 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none focus:bg-slate-700'
                    placeholder='Enter your username or email'
                    autoComplete='username'
                    required
                  />
                  <User className='absolute left-3 top-2.5 h-5 w-5 text-slate-400' />
                </div>
              </div>

              <Button type='submit' disabled={loading || !form.username} className='w-full'>
                {loading ? 'Sending Reset Code...' : 'Send Reset Code'}
              </Button>

              <div className='text-center space-y-2'>
                <button
                  type='button'
                  onClick={() => setStep('login')}
                  className='text-sm text-slate-400 hover:text-slate-300 transition-colors block w-full'
                >
                  ← Back to Login
                </button>
              </div>
            </form>
          )}

          {/* Reset Password with Code */}
          {step === 'reset-password' && (
            <form onSubmit={handleResetPassword} className='space-y-4'>
              <div>
                <label 
                  className='block text-sm font-medium mb-2'
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Confirmation Code *
                </label>
                <input
                  type='text'
                  name='confirmationCode'
                  value={form.confirmationCode}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border rounded focus:outline-none'
                  style={{
                    backgroundColor: 'var(--color-input-bg)',
                    borderColor: 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder='Enter the code from your email'
                  required
                />
                <p 
                  className='text-xs mt-1'
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Check your email for the confirmation code
                </p>
              </div>

              <div>
                <label 
                  className='block text-sm font-medium mb-2'
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  New Password *
                </label>
                <div className='relative'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name='newPassword'
                    value={form.newPassword}
                    onChange={handleChange}
                    className='w-full px-3 py-2 pr-10 border rounded focus:outline-none'
                    style={{
                      backgroundColor: 'var(--color-input-bg)',
                      borderColor: 'var(--color-input-border)',
                      color: 'var(--color-input-text)',
                    }}
                    placeholder='Enter new password'
                    autoComplete='new-password'
                    required
                  />
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className='absolute right-3 top-2.5'
                    style={{ color: 'var(--color-text-tertiary)' }}
                  >
                    {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
                  </button>
                </div>

                {/* Password strength indicator */}
                {form.newPassword && (
                  <div className='mt-2'>
                    <div className='flex items-center gap-2 mb-1'>
                      <div className='flex gap-1'>
                        {[1, 2, 3, 4].map(level => (
                          <div
                            key={level}
                            className={`h-1 w-8 rounded ${
                              level <= passwordStrength.strength
                                ? passwordStrength.color.replace('text-', 'bg-')
                                : 'bg-slate-600'
                            }`}
                          />
                        ))}
                      </div>
                      <span className={`text-xs ${passwordStrength.color}`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    <p 
                      className='text-xs'
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      Password must be at least 8 characters long
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label 
                  className='block text-sm font-medium mb-2'
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Confirm New Password *
                </label>
                <div className='relative'>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name='confirmPassword'
                    value={form.confirmPassword}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 pr-10 border rounded focus:outline-none ${
                      form.confirmPassword && form.newPassword !== form.confirmPassword
                        ? 'border-red-500 focus:border-red-500'
                        : ''
                    }`}
                    style={{
                      backgroundColor: 'var(--color-input-bg)',
                      borderColor: form.confirmPassword && form.newPassword !== form.confirmPassword
                        ? '#ef4444'
                        : 'var(--color-input-border)',
                      color: 'var(--color-input-text)',
                    }}
                    placeholder='Confirm new password'
                    autoComplete='off'
                    required
                  />
                  <button
                    type='button'
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className='absolute right-3 top-2.5'
                    style={{ color: 'var(--color-text-tertiary)' }}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className='h-5 w-5' />
                    ) : (
                      <Eye className='h-5 w-5' />
                    )}
                  </button>
                </div>

                {form.confirmPassword && form.newPassword !== form.confirmPassword && (
                  <p 
                    className='text-xs mt-1'
                    style={{ color: 'var(--color-error-text)' }}
                  >
                    Passwords do not match
                  </p>
                )}
              </div>

              <Button
                type='submit'
                disabled={
                  loading ||
                  !form.confirmationCode ||
                  !form.newPassword ||
                  !form.confirmPassword ||
                  form.newPassword !== form.confirmPassword
                }
                className='w-full'
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>

              <div className='text-center space-y-2'>
                <button
                  type='button'
                  onClick={() => setStep('forgot-password')}
                  className='text-sm text-slate-400 hover:text-slate-300 transition-colors block w-full'
                >
                  ← Back to Request
                </button>
                <button
                  type='button'
                  onClick={() => setStep('login')}
                  className='text-sm text-slate-400 hover:text-slate-300 transition-colors block w-full'
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}

          {/* Change Password */}
          {step === 'change-password' && (
            <form onSubmit={handleChangePassword} className='space-y-4'>
              {/* Hidden username field for accessibility */}
              <label htmlFor='hidden-username' className='sr-only'>
                Username
              </label>
              <input
                id='hidden-username'
                type='text'
                name='username'
                value={form.username}
                autoComplete='username'
                className='hidden'
                readOnly
              />
              {user && (
                <div className='p-3 bg-blue-500/20 rounded-lg mb-4'>
                  <p className='text-sm text-blue-400'>
                    Welcome, {user.firstName} {user.lastName}!
                  </p>
                  <p className='text-xs text-blue-300 mt-1'>
                    {session
                      ? 'Please set your permanent password to continue.'
                      : 'You can optionally change your password and set up MFA.'}
                  </p>
                </div>
              )}

              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>
                  New Password {session ? '*' : '(Optional)'}
                </label>
                <div className='relative'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name='newPassword'
                    value={form.newPassword}
                    onChange={handleChange}
                    className='w-full px-3 py-2 pr-10 bg-slate-700 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
                    placeholder={session ? 'Enter new password' : 'Enter new password (optional)'}
                    autoComplete='new-password'
                    required={!!session}
                  />
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className='absolute right-3 top-2.5 text-slate-400 hover:text-slate-300'
                  >
                    {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
                  </button>
                </div>

                {/* Password strength indicator */}
                {form.newPassword && (
                  <div className='mt-2'>
                    <div className='flex items-center gap-2 mb-1'>
                      <div className='flex gap-1'>
                        {[1, 2, 3, 4].map(level => (
                          <div
                            key={level}
                            className={`h-1 w-8 rounded ${
                              level <= passwordStrength.strength
                                ? passwordStrength.color.replace('text-', 'bg-')
                                : 'bg-slate-600'
                            }`}
                          />
                        ))}
                      </div>
                      <span className={`text-xs ${passwordStrength.color}`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    <p className='text-xs text-slate-500'>
                      Password must be at least 8 characters long
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>
                  Confirm New Password *
                </label>
                <div className='relative'>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name='confirmPassword'
                    value={form.confirmPassword}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 pr-10 bg-slate-700 border rounded text-slate-100 focus:outline-none ${
                      form.confirmPassword && form.newPassword !== form.confirmPassword
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-white/10 focus:border-blue-500'
                    }`}
                    placeholder='Confirm new password'
                    autoComplete='off'
                    required
                  />
                  <button
                    type='button'
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className='absolute right-3 top-2.5 text-slate-400 hover:text-slate-300'
                  >
                    {showConfirmPassword ? (
                      <EyeOff className='h-5 w-5' />
                    ) : (
                      <Eye className='h-5 w-5' />
                    )}
                  </button>
                </div>

                {form.confirmPassword && form.newPassword !== form.confirmPassword && (
                  <p className='text-xs text-red-400 mt-1'>Passwords do not match</p>
                )}
              </div>

              {/* MFA Setup Checkbox */}
              {/* MFA setup removed - should be done after login when user is authenticated */}

              <div className='flex gap-3'>
                <Button
                  type='submit'
                  disabled={
                    loading ||
                    !form.newPassword ||
                    !form.confirmPassword ||
                    form.newPassword !== form.confirmPassword
                  }
                  className='flex-1'
                >
                  {loading ? 'Setting Password...' : 'Set Password'}
                </Button>

                <Button
                  type='button'
                  onClick={() => {
                    // Clear any stored data and go back to login
                    localStorage.removeItem('cognito_access_token');
                    localStorage.removeItem('cognito_refresh_token');
                    localStorage.removeItem('cognito_id_token');
                    localStorage.removeItem('userType');
                    setStep('login');
                    setMessage('');
                    setForm({
                      username: '',
                      password: '',
                      tempPassword: '',
                      confirmationCode: '',
                      newPassword: '',
                      confirmPassword: '',
                    });
                  }}
                  variant='outline'
                  className='flex-1'
                >
                  Cancel
                </Button>
              </div>

              <div className='text-center space-y-2'>
                {/* Admin Portal Button - Only show for admin users */}
                {localStorage.getItem('userType') === 'admin' && (
                  <button
                    type='button'
                    onClick={() => navigate('/admin-dashboard')}
                    className='w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2'
                  >
                    <Shield className='w-4 h-4' />
                    Go to Admin Portal
                  </button>
                )}
                <button
                  type='button'
                  onClick={() => {
                    console.log('🔍 MFA Button Clicked - Debug Info:');
                    console.log('  - Current step:', step);
                    console.log('  - User:', user);
                    console.log('  - Session:', session);
                    console.log(
                      '  - Access Token:',
                      localStorage.getItem('cognito_access_token') ? 'Present' : 'Missing'
                    );
                    console.log(
                      '  - Refresh Token:',
                      localStorage.getItem('cognito_refresh_token') ? 'Present' : 'Missing'
                    );
                    console.log(
                      '  - ID Token:',
                      localStorage.getItem('cognito_id_token') ? 'Present' : 'Missing'
                    );
                    console.log('  - All localStorage keys:', Object.keys(localStorage));
                    setStep('mfa-setup');
                  }}
                  className='text-sm text-slate-400 hover:text-slate-300 transition-colors'
                >
                  Setup MFA (Optional)
                </button>
              </div>
            </form>
          )}

          {/* MFA Setup */}
          {step === 'mfa-setup' && (
            <div className='space-y-4'>
              {(() => {
                console.log('🔍 MFA Setup Step Rendered - Debug Info:');
                console.log('  - Step:', step);
                console.log('  - User:', user);
                console.log('  - Session:', session);
                console.log(
                  '  - Access Token:',
                  localStorage.getItem('cognito_access_token') ? 'Present' : 'Missing'
                );
                console.log('  - All localStorage keys:', Object.keys(localStorage));
                return null;
              })()}
              <div className='p-4 bg-blue-500/20 rounded-lg border border-blue-500/30'>
                <div className='flex items-start gap-3 mb-3'>
                  <CheckCircle className='h-6 w-6 text-blue-400 mt-0.5 flex-shrink-0' />
                  <div>
                    <h3 className='text-sm font-medium text-blue-200 mb-1'>
                      MFA Setup Instructions
                    </h3>
                    <ol className='text-xs text-blue-300 space-y-1'>
                      <li>1. Download an authenticator app (Google Authenticator, Authy, etc.)</li>
                      <li>2. Click "Start MFA Setup" to get your QR code and secret key</li>
                      <li>3. Scan the QR code or enter the secret key manually</li>
                      <li>4. Enter the 6-digit code from your app to complete setup</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className='text-center'>
                <Button onClick={handleMfaSetup} disabled={loading} className='w-full'>
                  {loading ? 'Starting MFA Setup...' : 'Start MFA Setup'}
                </Button>
              </div>

              {mfaSecret && (
                <form onSubmit={handleMfaVerification} className='space-y-4'>
                  <div className='p-4 bg-slate-800/50 rounded-lg border border-slate-600/50'>
                    <h4 className='text-sm font-medium text-slate-200 mb-3'>Setup Instructions:</h4>

                    {/* QR Code Display */}
                    {qrCodeDataUrl && (
                      <div className='text-center mb-4'>
                        <p className='text-xs text-slate-300 mb-2'>
                          Scan this QR code with your authenticator app:
                        </p>
                        <div className='inline-block p-3 bg-white rounded-lg shadow-lg border border-slate-600/20'>
                          <img src={qrCodeDataUrl} alt='MFA QR Code' className='w-48 h-48' />
                        </div>
                      </div>
                    )}

                    {/* Secret Key Display */}
                    <div className='mb-3'>
                      <h5 className='text-sm font-medium text-slate-200 mb-2'>
                        Or enter this secret key manually:
                      </h5>
                      <p className='text-xs text-slate-300 font-mono break-all bg-slate-700 p-2 rounded'>
                        {mfaSecret}
                      </p>
                    </div>

                    <p className='text-xs text-slate-400'>
                      Use either the QR code above or the secret key below to add this account to
                      your authenticator app.
                    </p>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>
                      MFA Code *
                    </label>
                    <div className='relative'>
                      <input
                        type='text'
                        name='mfaCode'
                        value={form.confirmationCode || ''}
                        onChange={e =>
                          setForm(prev => ({ ...prev, confirmationCode: e.target.value }))
                        }
                        className='w-full px-3 py-2 pl-12 bg-slate-700 border border-white/10 rounded text-slate-100 focus:border-blue-500 focus:outline-none'
                        placeholder='Enter 6-digit code from your authenticator app'
                        maxLength={6}
                        required
                      />
                      <Lock className='absolute left-3 top-2.5 h-5 w-5 text-slate-400' />
                    </div>
                    <p className='text-xs text-slate-500 mt-1'>
                      Enter the 6-digit code from your authenticator app
                    </p>
                  </div>

                  <Button
                    type='submit'
                    disabled={
                      loading || !form.confirmationCode || form.confirmationCode.length !== 6
                    }
                    className='w-full'
                  >
                    {loading ? 'Completing MFA Setup...' : 'Complete MFA Setup'}
                  </Button>
                </form>
              )}

              <div className='text-center space-y-2'>
                {/* Admin Portal Button - Only show for admin users */}
                {localStorage.getItem('userType') === 'admin' && (
                  <button
                    type='button'
                    onClick={() => navigate('/admin-dashboard')}
                    className='w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2'
                  >
                    <Shield className='w-4 h-4' />
                    Go to Admin Portal
                  </button>
                )}
                <button
                  type='button'
                  onClick={() => setStep('change-password')}
                  className='text-sm text-slate-400 hover:text-slate-300 transition-colors'
                >
                  ← Back to Password Setup
                </button>
              </div>
            </div>
          )}

          {/* Password Reset Success Splash */}
          {step === 'password-reset-success' && (
            <div className='space-y-6'>
              <div className='p-6 bg-green-500/20 rounded-lg border border-green-500/30'>
                <div className='flex items-center gap-3 mb-4'>
                  <CheckCircle className='h-8 w-8 text-green-400' />
                  <div>
                    <p className='text-xl font-semibold text-green-400'>🎉 Success!</p>
                    <p className='text-sm text-green-300'>Your password has been reset</p>
                  </div>
                </div>

                <div className='space-y-3 text-sm text-green-300'>
                  <p>✅ Your password has been successfully updated</p>
                  <p>🔒 You have been automatically logged out for security</p>
                  <p>🔑 You can now log in with your new password</p>
                </div>
              </div>

              <div className='p-4 bg-slate-800/50 rounded-lg border border-slate-600/50'>
                <p className='text-sm text-slate-300 text-center mb-4'>
                  Your password has been successfully updated. You can now close this window and log
                  in with your new password.
                </p>

                <div className='flex gap-3'>
                  <Button
                    onClick={() => {
                      // Reset form and go back to login
                      setForm({
                        username: form.username, // Keep the username
                        password: '',
                        tempPassword: '',
                        confirmationCode: '',
                        newPassword: '',
                        confirmPassword: '',
                      });
                      setStep('login');
                      setMessage('');
                    }}
                    className='flex-1'
                  >
                    Login with New Password
                  </Button>

                  <Button
                    onClick={() => {
                      // Redirect to password reset portal login page (stay on same domain)
                      const hostname = window.location.hostname;
                      let pwdResetUrl = 'https://dev-pwdreset.nghs.com';
                      if (hostname.includes('nghs.com')) {
                        pwdResetUrl = hostname.includes('dev-') 
                          ? 'https://dev-pwdreset.nghs.com' 
                          : 'https://pwdreset.nghs.com';
                      } else if (hostname.includes('optimumcloudservices.com')) {
                        pwdResetUrl = hostname.includes('dev-') 
                          ? 'https://dev-pwdreset.optimumcloudservices.com' 
                          : 'https://pwdreset.optimumcloudservices.com';
                      }
                      window.location.href = pwdResetUrl;
                    }}
                    className='flex-1 bg-slate-600 hover:bg-slate-700'
                  >
                    Back to Password Reset Portal
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Copyright with Easter egg */}
      <Copyright className='mt-8' showEasterEgg={true} />
    </div>
  );
}

/*
 * OHIT Watermark - Optimum Healthcare IT Transfer System
 * Copyright © 2025 Optimum Healthcare IT. All rights reserved.
 *
 * This file is part of the OHIT Admin Portal for secure healthcare file transfer.
 * Built with React, TypeScript, and AWS CDK for HIPAA-compliant operations.
 *
 * OHIT: Optimized for Health Information Technology
 * Secure, reliable, and compliant file transfer solutions for healthcare organizations.
 */
